// Sessione utente (localStorage) e chiamate all'API.
//
// La sessione e' composta da:
//   user    -> { googleUserInfo, jwt }  token ID di Google, usato come Bearer verso l'API
//   userDB  -> utente letto da /user, con i ruoli (Roles)

const API_URL = process.env.REACT_APP_API_URL;

// ---------- sessione ----------

export function getUser() {
    return JSON.parse(localStorage.getItem('user'));
}

export function getUserDB() {
    return JSON.parse(localStorage.getItem('userDB'));
}

export function clearSession() {
    localStorage.removeItem('user');
    localStorage.removeItem('userDB');
}

// Loggato se ha utente e ruoli e il token Google non e' scaduto (dura 1 ora).
export function isLoggedIn() {
    const user = getUser();
    return !!(user && getUserDB() && user.googleUserInfo.exp * 1000 > Date.now());
}

// Vero se l'utente ha almeno uno dei ruoli indicati.
export function hasRole(...roles) {
    return (getUserDB()?.Roles ?? []).some((r) => roles.includes(r.Role));
}

// Salva il token Google e carica l'utente dall'API. Se l'API rifiuta, la sessione viene cancellata.
export async function login(jwt) {
    localStorage.setItem('user', JSON.stringify({googleUserInfo: parseJwt(jwt), jwt}));
    try {
        const userDB = await apiGet('/user');
        localStorage.setItem('userDB', JSON.stringify(userDB));
    } catch (err) {
        clearSession();
        throw err;
    }
}

// Decodifica il payload di un JWT senza verificarlo (lo verifica l'API).
function parseJwt(token) {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(window.atob(base64))));
}

// ---------- chiamate API ----------

export class ApiError extends Error {
    constructor(status, message) {
        super(message);
        this.status = status;
    }
}

// Parametri vuoti (undefined, null, '') non vengono messi in query string.
function queryString(params) {
    const puliti = Object.entries(params ?? {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
    return puliti.length ? '?' + new URLSearchParams(puliti) : '';
}

// Chiamata autenticata. body (oggetto) viene mandato come JSON con il metodo indicato (default POST).
async function apiFetch(path, params, {method, body} = {}) {
    const headers = {Authorization: 'Bearer ' + getUser()?.jwt};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    const res = await fetch(API_URL + path + queryString(params), {
        method: method ?? (body !== undefined ? 'POST' : 'GET'),
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
        let message = `Errore API ${res.status}`;
        try {
            // {message} dall'API, {error, details} da NIL (e-commerce)
            const b = await res.json();
            const testo = b.msg ?? b.message ?? b.error ?? '';
            message += ': ' + testo + (b.details ? ` (${b.details})` : '');
        } catch {
            // corpo non JSON, teniamo solo lo status
        }
        throw new ApiError(res.status, message);
    }
    return res;
}

export async function apiGet(path, params) {
    return (await apiFetch(path, params)).json();
}

export async function apiPost(path, body, params) {
    return (await apiFetch(path, params, {body: body ?? {}})).json();
}

// Scarica un file autenticato e apre il "salva con nome" del browser.
// Senza filename usa quello suggerito dall'API (header Content-Disposition).
export async function apiDownload(path, params, filename) {
    const res = await apiFetch(path, params);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? nomeFile(res.headers.get('Content-Disposition')) ?? 'download';
    a.click();
    URL.revokeObjectURL(url);
}

// attachment; filename="fattura_12.pdf" -> fattura_12.pdf
function nomeFile(contentDisposition) {
    const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition ?? '');
    return m ? decodeURIComponent(m[1]) : null;
}

// Gestione uniforme degli errori: token non valido -> torna al login, altrimenti avvisa.
export function handleApiError(err) {
    console.error(err);
    if (err.status === 401) {
        alert('Sessione scaduta o non valida, effettua di nuovo il login. In caso di problemi contatta ict@noberasco.it');
        clearSession();
        window.location.assign('/login');
    } else {
        alert(`${err.message}\nSe il problema persiste contatta ict@noberasco.it`);
    }
}
